import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  FileText, Send, Clock, CheckCircle, XCircle, RefreshCw, Loader2, 
  Plus, Eye, ArrowDownCircle, ArrowUpCircle, Inbox, User,
  Calendar, MessageSquare, Download, Upload, X, File, Paperclip,
  ArrowUpDown, ArrowUp, ArrowDown, Trash2, Reply, Archive, RotateCcw, MoveRight
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Checkbox } from '../../components/ui/checkbox';
import { DialogDescription } from '../../components/ui/dialog';
import { InfoBanner } from '../../components/ui/info-banner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('fortis_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const BranchReports = ({ branchIdOverride, readOnly = false }) => {
  const { user } = useAuth();
  const [mainTab, setMainTab] = useState('incoming');
  
  // Reports state
  const [incomingReports, setIncomingReports] = useState([]);
  const [outgoingReports, setOutgoingReports] = useState([]);
  const [reportRequests, setReportRequests] = useState({ sent: [], received: [] });
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  
  // Okunmamış rapor sayısı
  // Okunmamış gelen rapor sayısı (kendi gönderdiği hariç)
  const unreadReportsCount = incomingReports.filter(r => 
    r.from_user_id !== user?.id && !r.read_by?.includes(user?.id)
  ).length;
  
  // Okunmamış rapor isteği sayısı (bana gelen istekler - sadece pending olanlar, kendi gönderdiği hariç)
  const unreadRequestsCount = (reportRequests.received || [])
    .filter(r => r.status === 'pending' && r.requester_id !== user?.id && !r.read_by?.includes(user?.id)).length;
  
  // Modal states
  const [createReportModalOpen, setCreateReportModalOpen] = useState(false);
  const [requestReportModalOpen, setRequestReportModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [respondModalOpen, setRespondModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedType, setSelectedType] = useState('');
  
  // Form states
  const [newReport, setNewReport] = useState({
    title: '',
    content: '',
    to_user_id: '',
    documents: []
  });
  
  const [newReportRequest, setNewReportRequest] = useState({
    target_user_id: '',
    title: '',
    description: '',
    due_date: ''
  });

  const [responseForm, setResponseForm] = useState({
    title: '',
    content: '',
    documents: []
  });

  // Sorting state
  const [reportSortConfig, setReportSortConfig] = useState({ key: null, direction: 'asc' });
  const [requestSortConfig, setRequestSortConfig] = useState({ key: null, direction: 'asc' });

  // Çoklu seçim state'leri
  const [selectedReports, setSelectedReports] = useState([]);
  const [selectedRequests, setSelectedRequests] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteType, setDeleteType] = useState(''); // 'report' veya 'request'
  const [deleting, setDeleting] = useState(false);

  // YENİ Badge için state - okunmamış bildirim olan öğeler
  const [unreadReportIds, setUnreadReportIds] = useState(new Set());

  // Arşiv state
  const [archivedReports, setArchivedReports] = useState([]);

  // Arşiv açıklaması modal state'leri
  const [archiveReasonModalOpen, setArchiveReasonModalOpen] = useState(false);
  const [archiveReason, setArchiveReason] = useState('');
  const [archiveTargetId, setArchiveTargetId] = useState(null);
  const [archivingItem, setArchivingItem] = useState(false);

  // Taşerona taşıma state'leri
  const [moveModalOpen, setMoveModalOpen] = useState(false);
  const [selectedReportToMove, setSelectedReportToMove] = useState(null);
  const [subcontractors, setSubcontractors] = useState([]);
  const [selectedSubcontractorId, setSelectedSubcontractorId] = useState('');

  useEffect(() => {
    if (mainTab === 'archive') {
      loadArchivedReports();
    } else {
      loadAll();
    }
    loadUsers();
    loadUnreadNotifications();
  }, [mainTab]);

  // Rapor isteği detayı açıldığında okundu olarak işaretle
  useEffect(() => {
    const markRequestAsRead = async () => {
      if (detailModalOpen && selectedType === 'request' && selectedItem?.id && selectedItem?.direction === 'received') {
        try {
          await axios.post(`${API}/work/report-requests/${selectedItem.id}/mark-read`, {}, { headers: getAuthHeaders() });
          // Badge sayacını güncelle
          window.dispatchEvent(new Event('unread-counts-updated'));
          // Listeyi yenile
          loadAll();
        } catch (error) {
          console.error('Okundu işaretleme hatası:', error);
        }
      }
    };
    markRequestAsRead();
  }, [detailModalOpen, selectedType, selectedItem]);

  // Rapor detayı açıldığında okundu olarak işaretle
  useEffect(() => {
    const markReportAsRead = async () => {
      // Gelen rapor detayı açıldığında ve kendi gönderdiği değilse okundu yap
      if (detailModalOpen && selectedType === 'report' && selectedItem?.id && selectedItem?.from_user_id !== user?.id) {
        try {
          await axios.post(`${API}/work/reports/${selectedItem.id}/mark-read`, {}, { headers: getAuthHeaders() });
          // Badge sayacını güncelle
          window.dispatchEvent(new Event('unread-counts-updated'));
          // Listeyi yenile
          loadAll();
        } catch (error) {
          console.error('Rapor okundu işaretleme hatası:', error);
        }
      }
    };
    markReportAsRead();
  }, [detailModalOpen, selectedType, selectedItem]);

  // YENİ Badge - Okunmamış bildirimleri yükle
  const loadUnreadNotifications = async () => {
    try {
      const res = await axios.get(`${API}/notifications`, {
        params: { category: 'raporlar', is_read: false },
        headers: getAuthHeaders()
      });
      const reportIds = new Set((res.data || []).map(n => n.related_id).filter(Boolean));
      setUnreadReportIds(reportIds);
    } catch (error) {
      console.error('Bildirimler yüklenirken hata:', error);
    }
  };

  // YENİ Badge - Bildirim okundu olarak işaretle
  const markReportNotificationAsRead = async (reportId) => {
    try {
      const res = await axios.get(`${API}/notifications`, {
        params: { category: 'raporlar', is_read: false },
        headers: getAuthHeaders()
      });
      const relatedNotifs = (res.data || []).filter(n => n.related_id === reportId);
      
      for (const notif of relatedNotifs) {
        await axios.put(`${API}/notifications/${notif.id}/read`, {}, { headers: getAuthHeaders() });
      }
      
      setUnreadReportIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(reportId);
        return newSet;
      });
      
      // Sidebar sayacını güncelle
      window.dispatchEvent(new Event('unread-counts-updated'));
    } catch (error) {
      console.error('Bildirim okundu yapılırken hata:', error);
    }
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const [inRes, outRes, reqRes] = await Promise.all([
        axios.get(`${API}/work/reports/incoming`, { headers }),
        axios.get(`${API}/work/reports/outgoing`, { headers }),
        axios.get(`${API}/work/report-requests/my`, { headers })
      ]);
      setIncomingReports(inRes.data || []);
      setOutgoingReports(outRes.data || []);
      setReportRequests(reqRes.data || { sent: [], received: [] });
    } catch (error) {
      console.error('Raporlar yüklenirken hata:', error);
      toast.error('Raporlar yüklenemedi');
    }
    setLoading(false);
  };

  const loadUsers = async () => {
    try {
      const res = await axios.get(`${API}/work/users`, { headers: getAuthHeaders() });
      setUsers(res.data || []);
    } catch (error) {
      console.error('Kullanıcılar yüklenirken hata:', error);
    }
  };

  // Arşivlenmiş raporları yükle
  const loadArchivedReports = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/work/reports/archived`, { headers: getAuthHeaders() });
      setArchivedReports(res.data || []);
    } catch (error) {
      console.error('Arşivlenmiş raporlar yüklenirken hata:', error);
    }
    setLoading(false);
  };

  // Raporu arşivle
  const openArchiveReasonModal = (reportId) => {
    setArchiveTargetId(reportId);
    setArchiveReason('');
    setArchiveReasonModalOpen(true);
  };

  const handleConfirmArchiveReport = async () => {
    if (!archiveReason.trim()) {
      toast.error('Arşiv açıklaması zorunludur');
      return;
    }
    
    setArchivingItem(true);
    try {
      await axios.delete(`${API}/work/reports/${archiveTargetId}`, { 
        headers: getAuthHeaders(),
        data: { archive_reason: archiveReason }
      });
      toast.success('Rapor arşivlendi');
      try {
        await loadAll();
      } catch (e) {
        console.log('Liste yenileme hatası:', e);
      }
      setArchiveReasonModalOpen(false);
      setArchiveReason('');
      setArchiveTargetId(null);
    } catch (error) {
      console.error('Arşivleme hatası:', error);
      toast.error('Rapor arşivlenemedi');
    } finally {
      setArchivingItem(false);
    }
  };

  const handleArchiveReport = async (reportId) => {
    openArchiveReasonModal(reportId);
  };

  // Raporu arşivden geri yükle
  const handleRestoreReport = async (reportId) => {
    try {
      const response = await axios.post(`${API}/work/reports/${reportId}/restore`, {}, { headers: getAuthHeaders() });
      if (response.status === 200) {
        toast.success('Rapor arşivden geri yüklendi');
        try {
          await loadArchivedReports();
        } catch (e) {
          console.log('Liste yenileme hatası:', e);
        }
      }
    } catch (error) {
      console.error('Geri yükleme hatası:', error);
      toast.error('Rapor geri yüklenemedi');
    }
  };

  // Taşeronları yükle
  const loadSubcontractors = async () => {
    try {
      const res = await axios.get(`${API}/branch/subcontractors`, { headers: getAuthHeaders() });
      setSubcontractors(res.data || []);
    } catch (error) {
      console.error('Taşeronlar yüklenirken hata:', error);
    }
  };

  // Raporu taşerona taşı
  const handleMoveToSubcontractor = async () => {
    if (!selectedSubcontractorId || !selectedReportToMove) {
      toast.error('Lütfen bir taşeron seçin');
      return;
    }
    
    try {
      const response = await axios.post(
        `${API}/work/reports/${selectedReportToMove.id}/move-to-subcontractor`,
        { subcontractor_id: selectedSubcontractorId },
        { headers: getAuthHeaders() }
      );
      if (response.status === 200) {
        toast.success('Rapor taşerona taşındı');
        setMoveModalOpen(false);
        setSelectedReportToMove(null);
        setSelectedSubcontractorId('');
        loadAll();
      }
    } catch (error) {
      console.error('Taşıma hatası:', error);
      toast.error(error.response?.data?.detail || 'Rapor taşınamadı');
    }
  };

  // Taşı modal'ını aç
  const openMoveModal = (report) => {
    setSelectedReportToMove(report);
    loadSubcontractors();
    setMoveModalOpen(true);
  };

  const handleCreateReport = async () => {
    if (!newReport.title) {
      toast.error('Rapor başlığı gerekli');
      return;
    }
    
    try {
      const reportData = {
        ...newReport,
        to_user_id: newReport.to_user_id === 'none' ? '' : newReport.to_user_id
      };
      await axios.post(`${API}/work/reports`, reportData, { headers: getAuthHeaders() });
      toast.success('Rapor oluşturuldu');
      setCreateReportModalOpen(false);
      setNewReport({ title: '', content: '', to_user_id: '', documents: [] });
      loadAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Rapor oluşturulamadı');
    }
  };

  // Belge yükleme handler
  const handleReportDocUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = () => {
        setNewReport(prev => ({
          ...prev,
          documents: [...prev.documents, {
            name: file.name,
            type: file.type,
            size: file.size,
            data: reader.result
          }]
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const removeReportDocument = (index) => {
    setNewReport(prev => ({
      ...prev,
      documents: prev.documents.filter((_, i) => i !== index)
    }));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleRequestReport = async () => {
    if (!newReportRequest.title || !newReportRequest.target_user_id) {
      toast.error('Rapor başlığı ve hedef kullanıcı gerekli');
      return;
    }
    
    try {
      await axios.post(`${API}/work/report-requests`, newReportRequest, { headers: getAuthHeaders() });
      toast.success('Rapor isteği gönderildi');
      setRequestReportModalOpen(false);
      setNewReportRequest({ target_user_id: '', title: '', description: '', due_date: '' });
      loadAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Rapor isteği gönderilemedi');
    }
  };

  const handleUpdateReportRequest = async (requestId, status) => {
    try {
      await axios.put(`${API}/work/report-requests/${requestId}`, null, {
        params: { status },
        headers: getAuthHeaders()
      });
      toast.success('Rapor isteği güncellendi');
      loadAll();
      setDetailModalOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Rapor isteği güncellenemedi');
    }
  };

  // Rapor isteğine cevap verme
  const handleOpenRespondModal = (request) => {
    setSelectedItem(request);
    setResponseForm({
      title: `RE: ${request.title}`,
      content: '',
      documents: []
    });
    setDetailModalOpen(false);
    setRespondModalOpen(true);
  };

  const handleResponseDocUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = () => {
        setResponseForm(prev => ({
          ...prev,
          documents: [...prev.documents, {
            name: file.name,
            type: file.type,
            size: file.size,
            data: reader.result
          }]
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const removeResponseDocument = (index) => {
    setResponseForm(prev => ({
      ...prev,
      documents: prev.documents.filter((_, i) => i !== index)
    }));
  };

  const handleSubmitResponse = async () => {
    if (!responseForm.title) {
      toast.error('Rapor başlığı gerekli');
      return;
    }
    
    try {
      await axios.post(
        `${API}/work/report-requests/${selectedItem.id}/respond`,
        responseForm,
        { headers: getAuthHeaders() }
      );
      toast.success('Rapor isteğine cevap verildi');
      setRespondModalOpen(false);
      setResponseForm({ title: '', content: '', documents: [] });
      loadAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Cevap gönderilemedi');
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      draft: { label: 'Taslak', color: 'bg-gray-100 text-gray-700', icon: FileText },
      sent: { label: 'Gönderildi', color: 'bg-blue-100 text-blue-700', icon: Send },
      pending: { label: 'Bekliyor', color: 'bg-amber-100 text-amber-700', icon: Clock },
      accepted: { label: 'Kabul Edildi', color: 'bg-green-100 text-green-700', icon: CheckCircle },
      rejected: { label: 'Reddedildi', color: 'bg-red-100 text-red-700', icon: XCircle },
      read: { label: 'Okundu', color: 'bg-blue-100 text-blue-700', icon: Eye },
      approved: { label: 'Onaylandı', color: 'bg-green-100 text-green-700', icon: CheckCircle },
      completed: { label: 'Cevaplandı', color: 'bg-purple-100 text-purple-700', icon: Reply }
    };
    const c = config[status] || config.pending;
    const Icon = c.icon;
    return (
      <Badge className={`${c.color} gap-1`}>
        <Icon className="h-3 w-3" />
        {c.label}
      </Badge>
    );
  };

  // Çoklu seçim fonksiyonları
  const toggleReportSelection = (reportId) => {
    setSelectedReports(prev => 
      prev.includes(reportId) 
        ? prev.filter(id => id !== reportId)
        : [...prev, reportId]
    );
  };

  const toggleRequestSelection = (requestId) => {
    setSelectedRequests(prev => 
      prev.includes(requestId) 
        ? prev.filter(id => id !== requestId)
        : [...prev, requestId]
    );
  };

  const selectAllReports = (reports) => {
    const reportIds = reports.map(r => r.id);
    const allSelected = reportIds.every(id => selectedReports.includes(id));
    if (allSelected) {
      setSelectedReports(prev => prev.filter(id => !reportIds.includes(id)));
    } else {
      setSelectedReports(prev => [...new Set([...prev, ...reportIds])]);
    }
  };

  const selectAllRequests = (requests) => {
    const requestIds = requests.map(r => r.id);
    const allSelected = requestIds.every(id => selectedRequests.includes(id));
    if (allSelected) {
      setSelectedRequests(prev => prev.filter(id => !requestIds.includes(id)));
    } else {
      setSelectedRequests(prev => [...new Set([...prev, ...requestIds])]);
    }
  };

  const openDeleteModal = (type) => {
    setDeleteType(type);
    setShowDeleteModal(true);
  };

  const handleDeleteSelected = async () => {
    const idsToDelete = deleteType === 'report' ? selectedReports : selectedRequests;
    if (idsToDelete.length === 0) return;
    
    setDeleting(true);
    try {
      let successCount = 0;
      let failCount = 0;
      
      for (const id of idsToDelete) {
        try {
          const endpoint = deleteType === 'report' 
            ? `${API}/work/reports/${id}`
            : `${API}/work/report-requests/${id}`;
          await axios.delete(endpoint, { headers: getAuthHeaders() });
          successCount++;
        } catch (error) {
          failCount++;
        }
      }
      
      if (successCount > 0) {
        toast.success(`${successCount} ${deleteType === 'report' ? 'rapor' : 'istek'} silindi`);
        loadAll();
      }
      if (failCount > 0) {
        toast.error(`${failCount} öğe silinemedi`);
      }
      
      if (deleteType === 'report') {
        setSelectedReports([]);
      } else {
        setSelectedRequests([]);
      }
      setShowDeleteModal(false);
    } catch (error) {
      toast.error('Silme işlemi sırasında hata oluştu');
    } finally {
      setDeleting(false);
    }
  };

  // Sıralama fonksiyonları
  const handleReportSort = (key) => {
    setReportSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleRequestSort = (key) => {
    setRequestSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Sıralama ikonu
  const SortIcon = ({ columnKey, sortConfig }) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown className="h-4 w-4 text-gray-400" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="h-4 w-4 text-amber-600" />
      : <ArrowDown className="h-4 w-4 text-amber-600" />;
  };

  // Raporlar için sıralama
  const sortReports = (reports, type) => {
    if (!reportSortConfig.key) return reports;
    
    return [...reports].sort((a, b) => {
      let aValue, bValue;
      
      switch (reportSortConfig.key) {
        case 'title':
          aValue = a.title?.toLowerCase() || '';
          bValue = b.title?.toLowerCase() || '';
          break;
        case 'sender':
          if (type === 'incoming') {
            aValue = (a.from_user?.name || a.sender_name || '').toLowerCase();
            bValue = (b.from_user?.name || b.sender_name || '').toLowerCase();
          } else {
            aValue = (a.to_user?.name || a.recipient_name || '').toLowerCase();
            bValue = (b.to_user?.name || b.recipient_name || '').toLowerCase();
          }
          break;
        case 'status':
          const statusOrder = { approved: 5, accepted: 4, read: 3, sent: 2, pending: 1, draft: 0, rejected: -1 };
          aValue = statusOrder[a.status] || 0;
          bValue = statusOrder[b.status] || 0;
          break;
        case 'created_at':
          aValue = new Date(a.created_at || 0).getTime();
          bValue = new Date(b.created_at || 0).getTime();
          break;
        default:
          return 0;
      }
      
      if (aValue < bValue) return reportSortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return reportSortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Rapor istekleri için sıralama
  const sortRequests = (requests) => {
    if (!requestSortConfig.key) return requests;
    
    return [...requests].sort((a, b) => {
      let aValue, bValue;
      
      switch (requestSortConfig.key) {
        case 'title':
          aValue = a.title?.toLowerCase() || '';
          bValue = b.title?.toLowerCase() || '';
          break;
        case 'user':
          aValue = (a.from_user?.name || a.to_user?.name || '').toLowerCase();
          bValue = (b.from_user?.name || b.to_user?.name || '').toLowerCase();
          break;
        case 'created_at':
          aValue = new Date(a.created_at || 0).getTime();
          bValue = new Date(b.created_at || 0).getTime();
          break;
        case 'due_date':
          aValue = a.due_date ? new Date(a.due_date).getTime() : 0;
          bValue = b.due_date ? new Date(b.due_date).getTime() : 0;
          break;
        case 'status':
          const statusOrder = { approved: 4, accepted: 3, sent: 2, pending: 1, rejected: 0 };
          aValue = statusOrder[a.status] || 0;
          bValue = statusOrder[b.status] || 0;
          break;
        default:
          return 0;
      }
      
      if (aValue < bValue) return requestSortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return requestSortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const renderReportTable = (reports, type) => {
    if (reports.length === 0) {
      return (
        <Card>
          <CardContent className="p-8 text-center">
            <FileText className="h-16 w-16 mx-auto mb-4 text-gray-200" />
            <h3 className="text-xl font-bold mb-2">Rapor Bulunmuyor</h3>
            <p className="text-gray-500">Bu kategoride rapor yok</p>
          </CardContent>
        </Card>
      );
    }

    const sortedReports = sortReports(reports, type);
    const currentSelected = selectedReports.filter(id => reports.some(r => r.id === id));

    return (
      <Card>
        <CardContent className="p-0">
          {/* Seçim Toolbar */}
          <div className="flex items-center justify-between p-3 border-b">
            <div className="flex items-center gap-3">
              <Checkbox
                checked={reports.length > 0 && reports.every(r => selectedReports.includes(r.id))}
                onCheckedChange={() => selectAllReports(reports)}
              />
              <span className="text-sm text-gray-500">
                {currentSelected.length > 0 ? `${currentSelected.length} seçili` : 'Tümünü seç'}
              </span>
            </div>
            {currentSelected.length > 0 && (
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setSelectedReports([])}>
                  Seçimi Temizle
                </Button>
                <Button variant="destructive" size="sm" onClick={() => openDeleteModal('report')} className="gap-1">
                  <Trash2 className="h-4 w-4" />
                  Sil ({currentSelected.length})
                </Button>
              </div>
            )}
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10"></TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => handleReportSort('title')}
                >
                  <div className="flex items-center gap-1">
                    Rapor Başlığı
                    <SortIcon columnKey="title" sortConfig={reportSortConfig} />
                  </div>
                </TableHead>
                <TableHead>Rapor İçeriği</TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => handleReportSort('sender')}
                >
                  <div className="flex items-center gap-1">
                    {type === 'incoming' ? 'Gönderen' : 'Alıcı'}
                    <SortIcon columnKey="sender" sortConfig={reportSortConfig} />
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => handleReportSort('status')}
                >
                  <div className="flex items-center gap-1">
                    Durum
                    <SortIcon columnKey="status" sortConfig={reportSortConfig} />
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => handleReportSort('created_at')}
                >
                  <div className="flex items-center gap-1">
                    Tarih
                    <SortIcon columnKey="created_at" sortConfig={reportSortConfig} />
                  </div>
                </TableHead>
                <TableHead className="text-right">İşlemler</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedReports.map(report => {
                const isUnread = !report.read_by?.includes(user?.id) && report.from_user_id !== user?.id;
                return (
                <TableRow 
                  key={report.id} 
                  data-testid={`report-row-${report.id}`}
                  className={`${selectedReports.includes(report.id) ? 'bg-amber-50' : ''} ${isUnread ? 'bg-blue-50' : ''}`}
                  onClick={() => {
                    if (isUnread) {
                      markReportNotificationAsRead(report.id);
                    }
                  }}
                >
                  <TableCell>
                    <Checkbox
                      checked={selectedReports.includes(report.id)}
                      onCheckedChange={() => toggleReportSelection(report.id)}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <p className="font-medium">{report.title}</p>
                      {isUnread && (
                        <Badge className="bg-blue-500 text-white text-xs animate-pulse">YENİ</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {report.content ? (
                      <p className="text-sm text-gray-500 truncate max-w-[200px]">{report.content}</p>
                    ) : (
                      <span className="text-gray-400 text-sm">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-gray-400" />
                      {type === 'incoming' ? report.from_user?.name || report.sender_name : report.to_user?.name || report.recipient_name || 'Belirtilmemiş'}
                    </div>
                  </TableCell>
                  <TableCell>{getStatusBadge(report.status)}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 text-sm text-gray-500">
                      <Calendar className="h-4 w-4" />
                      <span>{new Date(report.created_at).toLocaleDateString('tr-TR')}</span>
                      <span className="text-gray-400">-</span>
                      <Clock className="h-3 w-3" />
                      <span>{new Date(report.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => { setSelectedItem(report); setSelectedType('report'); setDetailModalOpen(true); }}
                        data-testid={`view-report-${report.id}`}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        Detay
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        title="Taşerona Taşı"
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        onClick={() => openMoveModal(report)}
                        data-testid={`move-report-${report.id}`}
                      >
                        <MoveRight className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        title="Arşivle"
                        className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                        onClick={() => handleArchiveReport(report.id)}
                        data-testid={`archive-report-${report.id}`}
                      >
                        <Archive className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              )})}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    );
  };

  const renderRequestTable = (requests, direction) => {
    // Sadece pending olanları göster - received için
    const filteredRequests = direction === 'received' 
      ? requests.filter(req => req.status === 'pending')
      : requests;
    
    if (filteredRequests.length === 0) {
      return (
        <Card>
          <CardContent className="p-8 text-center">
            <MessageSquare className="h-16 w-16 mx-auto mb-4 text-gray-200" />
            <h3 className="text-xl font-bold mb-2">Rapor İsteği Bulunmuyor</h3>
            <p className="text-gray-500">{direction === 'sent' ? 'Gönderdiğiniz rapor isteği yok' : 'Bekleyen gelen isteğiniz yok'}</p>
          </CardContent>
        </Card>
      );
    }

    const sortedRequests = sortRequests(filteredRequests);
    const currentSelected = selectedRequests.filter(id => filteredRequests.some(r => r.id === id));

    return (
      <Card>
        <CardContent className="p-0">
          {/* Seçim Toolbar */}
          <div className="flex items-center justify-between p-3 border-b">
            <div className="flex items-center gap-3">
              <Checkbox
                checked={filteredRequests.length > 0 && filteredRequests.every(r => selectedRequests.includes(r.id))}
                onCheckedChange={() => selectAllRequests(filteredRequests)}
              />
              <span className="text-sm text-gray-500">
                {currentSelected.length > 0 ? `${currentSelected.length} seçili` : 'Tümünü seç'}
              </span>
            </div>
            {currentSelected.length > 0 && (
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setSelectedRequests([])}>
                  Seçimi Temizle
                </Button>
                <Button variant="destructive" size="sm" onClick={() => openDeleteModal('request')} className="gap-1">
                  <Trash2 className="h-4 w-4" />
                  Sil ({currentSelected.length})
                </Button>
              </div>
            )}
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10"></TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => handleRequestSort('title')}
                >
                  <div className="flex items-center gap-1">
                    İstek Konusu
                    <SortIcon columnKey="title" sortConfig={requestSortConfig} />
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => handleRequestSort('user')}
                >
                  <div className="flex items-center gap-1">
                    {direction === 'sent' ? 'Kime' : 'Kimden'}
                    <SortIcon columnKey="user" sortConfig={requestSortConfig} />
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => handleRequestSort('created_at')}
                >
                  <div className="flex items-center gap-1">
                    Oluşturulma Tarihi
                    <SortIcon columnKey="created_at" sortConfig={requestSortConfig} />
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => handleRequestSort('due_date')}
                >
                  <div className="flex items-center gap-1">
                    Son Tarih
                    <SortIcon columnKey="due_date" sortConfig={requestSortConfig} />
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => handleRequestSort('status')}
                >
                  <div className="flex items-center gap-1">
                    Durum
                    <SortIcon columnKey="status" sortConfig={requestSortConfig} />
                  </div>
                </TableHead>
                <TableHead className="text-right">İşlemler</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedRequests.map(req => {
                const isUnread = direction === 'received' && !req.read_by?.includes(user?.id);
                return (
                <TableRow 
                  key={req.id} 
                  data-testid={`request-row-${req.id}`}
                  className={isUnread ? 'bg-purple-50' : selectedRequests.includes(req.id) ? 'bg-amber-50' : ''}
                >
                  <TableCell>
                    <Checkbox
                      checked={selectedRequests.includes(req.id)}
                      onCheckedChange={() => toggleRequestSelection(req.id)}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div>
                        <p className="font-medium">{req.title}</p>
                        {req.description && (
                          <p className="text-sm text-gray-500 truncate max-w-[200px]">{req.description}</p>
                        )}
                      </div>
                      {isUnread && (
                        <Badge className="bg-purple-500 text-white text-xs animate-pulse">YENİ</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-gray-400" />
                      {direction === 'sent' ? req.target_user?.name : req.requester?.name}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 text-sm text-gray-500">
                      <Calendar className="h-4 w-4" />
                      <span>{new Date(req.created_at).toLocaleDateString('tr-TR')}</span>
                      <span className="text-gray-400">-</span>
                      <Clock className="h-3 w-3" />
                      <span>{new Date(req.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    {req.due_date ? (
                      <div className="flex items-center gap-1 text-sm">
                        <Calendar className="h-4 w-4 text-gray-400" />
                        {new Date(req.due_date).toLocaleDateString('tr-TR')}
                      </div>
                    ) : '-'}
                  </TableCell>
                  <TableCell>{getStatusBadge(req.status)}</TableCell>
                  <TableCell className="text-right">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => { setSelectedItem({...req, direction}); setSelectedType('request'); setDetailModalOpen(true); }}
                      data-testid={`view-request-${req.id}`}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      Detay
                    </Button>
                  </TableCell>
                </TableRow>
              )})}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6" data-testid="branch-reports">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">RAPORLAR</h1>
          <p className="text-gray-500">Şube raporlarını yönetin</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={loadAll} variant="outline" size="sm" className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Yenile
          </Button>
          <Button 
            onClick={() => setRequestReportModalOpen(true)} 
            variant="outline"
            className="gap-2"
            data-testid="request-report-btn"
          >
            <MessageSquare className="h-4 w-4" />
            RAPOR İSTE
          </Button>
          <Button 
            onClick={() => setCreateReportModalOpen(true)} 
            className="gap-2 bg-[hsl(var(--brand-700))] hover:bg-[hsl(var(--brand-800))]"
            data-testid="create-report-btn"
          >
            <Send className="h-4 w-4" />
            RAPOR GÖNDER
          </Button>
        </div>
      </div>

      {/* InfoBanner */}
      <InfoBanner storageKey="branch-reports-info">
        Raporlar sayfasından ihtiyacınız olan raporları talep edebilir, iletmeniz gereken raporları iletebilir veya gelen raporlarınızı buradan görüntüleyebilirsiniz.
      </InfoBanner>

      {/* İstatistik Kartları */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4 flex items-center gap-3">
            <ArrowDownCircle className="h-8 w-8 text-blue-600" />
            <div>
              <p className="text-2xl font-bold text-blue-700">{incomingReports.length}</p>
              <p className="text-sm text-blue-600">Gelen Rapor</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-orange-50 border-orange-200">
          <CardContent className="p-4 flex items-center gap-3">
            <ArrowUpCircle className="h-8 w-8 text-orange-600" />
            <div>
              <p className="text-2xl font-bold text-orange-700">{outgoingReports.length}</p>
              <p className="text-sm text-orange-600">Giden Rapor</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-purple-50 border-purple-200">
          <CardContent className="p-4 flex items-center gap-3">
            <MessageSquare className="h-8 w-8 text-purple-600" />
            <div>
              <p className="text-2xl font-bold text-purple-700">
                {(reportRequests.sent?.length || 0) + (reportRequests.received?.length || 0)}
              </p>
              <p className="text-sm text-purple-600">Rapor İsteği</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
        </div>
      ) : (
        <Tabs value={mainTab} onValueChange={setMainTab} className="w-full">
          <TabsList className="grid w-full grid-cols-4 mb-4">
            <TabsTrigger value="incoming" className="gap-2" data-testid="incoming-reports-tab">
              <ArrowDownCircle className="h-4 w-4" />
              GELEN RAPORLAR
              {incomingReports.length > 0 && (
                <Badge className="ml-1 bg-blue-500 text-white">{incomingReports.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="outgoing" className="gap-2" data-testid="outgoing-reports-tab">
              <ArrowUpCircle className="h-4 w-4" />
              GİDEN RAPORLAR
              {outgoingReports.length > 0 && (
                <Badge className="ml-1 bg-orange-500 text-white">{outgoingReports.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="requests" className="gap-2" data-testid="report-requests-tab">
              <MessageSquare className="h-4 w-4" />
              RAPOR İSTEKLERİ
              {unreadRequestsCount > 0 && (
                <Badge className="ml-1 bg-purple-500 text-white animate-pulse">{unreadRequestsCount} yeni</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="archive" className="gap-2" data-testid="archive-reports-tab">
              <Archive className="h-4 w-4" />
              ARŞİV ({archivedReports.length})
            </TabsTrigger>
          </TabsList>

          {/* GELEN RAPORLAR */}
          <TabsContent value="incoming" className="mt-4">
            {renderReportTable(incomingReports, 'incoming')}
          </TabsContent>

          {/* GİDEN RAPORLAR */}
          <TabsContent value="outgoing" className="mt-4">
            {renderReportTable(outgoingReports, 'outgoing')}
          </TabsContent>

          {/* RAPOR İSTEKLERİ */}
          <TabsContent value="requests" className="mt-4">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Send className="h-5 w-5 text-purple-600" />
                    Gönderdiğim İstekler
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {renderRequestTable(reportRequests.sent || [], 'sent')}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <ArrowDownCircle className="h-5 w-5 text-blue-600" />
                    Bana Gelen İstekler
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {renderRequestTable(reportRequests.received || [], 'received')}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* ARŞİV */}
          <TabsContent value="archive" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-gray-600">
                  <Archive className="h-5 w-5" />
                  Arşivlenmiş Raporlar
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {archivedReports.length === 0 ? (
                  <div className="text-center py-12">
                    <Archive className="h-16 w-16 mx-auto mb-4 text-gray-200" />
                    <h3 className="text-xl font-bold mb-2 text-gray-600">Arşivde Rapor Yok</h3>
                    <p className="text-gray-500">Arşivlenmiş rapor bulunmuyor</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead className="font-bold">Rapor</TableHead>
                        <TableHead className="font-bold">Gönderen</TableHead>
                        <TableHead className="font-bold">Alan</TableHead>
                        <TableHead className="font-bold">Tarih</TableHead>
                        <TableHead className="font-bold">Arşiv Tarihi</TableHead>
                        <TableHead className="font-bold">İşlemler</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {archivedReports.map(report => (
                        <TableRow key={report.id} className="hover:bg-gray-50">
                          <TableCell>
                            <div className="font-medium">{report.title}</div>
                            {report.content && (
                              <div className="text-sm text-gray-500 truncate max-w-[200px]">{report.content}</div>
                            )}
                          </TableCell>
                          <TableCell>{report.from_user?.name || report.from_user_name || '-'}</TableCell>
                          <TableCell>{report.to_user?.name || report.to_user_name || '-'}</TableCell>
                          <TableCell>
                            {report.created_at ? new Date(report.created_at).toLocaleDateString('tr-TR', {
                              day: '2-digit', month: '2-digit', year: 'numeric'
                            }) : '-'}
                          </TableCell>
                          <TableCell>
                            {report.archived_at ? new Date(report.archived_at).toLocaleDateString('tr-TR', {
                              day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
                            }) : '-'}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button 
                                variant="outline" 
                                size="sm" 
                                title="Görüntüle"
                                onClick={() => { setSelectedItem(report); setSelectedType('report'); setDetailModalOpen(true); }}
                                data-testid={`view-archived-report-${report.id}`}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm" 
                                title="Geri Yükle"
                                className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                onClick={() => handleRestoreReport(report.id)}
                                data-testid={`restore-report-${report.id}`}
                              >
                                <RotateCcw className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}

      {/* Rapor Oluşturma Modalı */}
      <Dialog open={createReportModalOpen} onOpenChange={setCreateReportModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-amber-600" />
              Yeni Rapor Oluştur
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="report-title">Rapor Başlığı *</Label>
              <Input 
                id="report-title"
                value={newReport.title}
                onChange={(e) => setNewReport({ ...newReport, title: e.target.value })}
                placeholder="Rapor başlığını girin"
                data-testid="report-title-input"
              />
            </div>
            
            <div>
              <Label htmlFor="report-content">Rapor İçeriği</Label>
              <Textarea 
                id="report-content"
                value={newReport.content}
                onChange={(e) => setNewReport({ ...newReport, content: e.target.value })}
                placeholder="Rapor içeriği"
                rows={5}
                data-testid="report-content-input"
              />
            </div>
            
            <div>
              <Label htmlFor="report-user">Gönderilecek Kişi (Opsiyonel)</Label>
              <Select 
                value={newReport.to_user_id} 
                onValueChange={(val) => setNewReport({ ...newReport, to_user_id: val })}
              >
                <SelectTrigger data-testid="report-user-select">
                  <SelectValue placeholder="Kullanıcı seçin (boş bırakılabilir)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Belirtilmemiş</SelectItem>
                  {users.map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Belge Yükleme Alanı */}
            <div>
              <Label className="flex items-center gap-2">
                <Paperclip className="h-4 w-4 text-blue-500" />
                Belge Ekle
              </Label>
              <div className="mt-2">
                <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50 border-gray-300">
                  <div className="flex flex-col items-center justify-center pt-2 pb-2">
                    <Upload className="w-6 h-6 mb-1 text-gray-400" />
                    <p className="text-sm text-gray-500">Belge yüklemek için tıklayın</p>
                    <p className="text-xs text-gray-400">PDF, JPG, PNG, DOC, XLS</p>
                  </div>
                  <input
                    type="file"
                    className="hidden"
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                    onChange={handleReportDocUpload}
                    data-testid="report-file-input"
                  />
                </label>
              </div>
              {newReport.documents.length > 0 && (
                <div className="mt-2 space-y-2">
                  {newReport.documents.map((doc, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex items-center gap-2">
                        <File className="h-4 w-4 text-blue-500" />
                        <span className="text-sm truncate max-w-[200px]">{doc.name}</span>
                        <span className="text-xs text-gray-400">({formatFileSize(doc.size)})</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeReportDocument(index)}
                        className="text-red-500 hover:text-red-700 p-1"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateReportModalOpen(false)}>
              İptal
            </Button>
            <Button 
              onClick={handleCreateReport}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="submit-report-btn"
            >
              <Send className="h-4 w-4 mr-2" />
              Rapor Gönder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rapor İsteği Modalı */}
      <Dialog open={requestReportModalOpen} onOpenChange={setRequestReportModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-purple-600" />
              Rapor İste
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="request-user">Kimden İstiyorsunuz? *</Label>
              <Select 
                value={newReportRequest.target_user_id} 
                onValueChange={(val) => setNewReportRequest({ ...newReportRequest, target_user_id: val })}
              >
                <SelectTrigger data-testid="request-user-select">
                  <SelectValue placeholder="Kullanıcı seçin" />
                </SelectTrigger>
                <SelectContent>
                  {users.map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="request-title">Rapor Konusu *</Label>
              <Input 
                id="request-title"
                value={newReportRequest.title}
                onChange={(e) => setNewReportRequest({ ...newReportRequest, title: e.target.value })}
                placeholder="İstenen raporun konusu"
                data-testid="request-title-input"
              />
            </div>
            
            <div>
              <Label htmlFor="request-desc">Açıklama</Label>
              <Textarea 
                id="request-desc"
                value={newReportRequest.description}
                onChange={(e) => setNewReportRequest({ ...newReportRequest, description: e.target.value })}
                placeholder="Rapor hakkında detaylı açıklama"
                rows={3}
                data-testid="request-desc-input"
              />
            </div>
            
            <div>
              <Label htmlFor="request-due">Son Tarih</Label>
              <Input 
                id="request-due"
                type="date"
                value={newReportRequest.due_date}
                onChange={(e) => setNewReportRequest({ ...newReportRequest, due_date: e.target.value })}
                data-testid="request-due-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setRequestReportModalOpen(false)}>
              İptal
            </Button>
            <Button 
              onClick={handleRequestReport}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="submit-request-btn"
            >
              <Send className="h-4 w-4 mr-2" />
              İsteği Gönder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detay Modalı */}
      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-amber-600" />
              {selectedType === 'report' ? 'Rapor Detayı' : 'Rapor İsteği Detayı'}
            </DialogTitle>
          </DialogHeader>
          
          {selectedItem && selectedType === 'report' && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="h-4 w-4 text-amber-600" />
                  <span className="font-semibold text-gray-700">Rapor</span>
                </div>
                <h3 className="font-bold text-lg mb-2">{selectedItem.title}</h3>
                {selectedItem.content && (
                  <p className="text-gray-600 whitespace-pre-wrap">{selectedItem.content}</p>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Gönderen</Label>
                  <p className="font-medium">{selectedItem.from_user?.name || selectedItem.from_user_name || selectedItem.sender_name || '-'}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Alıcı</Label>
                  <p className="font-medium">{selectedItem.to_user?.name || selectedItem.to_user_name || selectedItem.recipient_name || 'Belirtilmemiş'}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Durum</Label>
                  <div className="mt-1">{getStatusBadge(selectedItem.status)}</div>
                </div>
                <div>
                  <Label className="text-gray-500">Tarih ve Saat</Label>
                  <p className="font-medium">
                    {new Date(selectedItem.created_at).toLocaleDateString('tr-TR')} - {new Date(selectedItem.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>

              {/* Arşiv Bilgileri */}
              {selectedItem.is_archived && (
                <div className="pt-4 border-t bg-orange-50 p-4 rounded-lg border border-orange-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Archive className="h-4 w-4 text-orange-600" />
                    <span className="font-semibold text-orange-700">Arşiv Bilgileri</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <Label className="text-gray-500">Arşivleyen</Label>
                      <p className="font-medium">{selectedItem.archived_by_name || selectedItem.archived_by || '-'}</p>
                    </div>
                    <div>
                      <Label className="text-gray-500">Arşiv Tarihi</Label>
                      <p className="font-medium">
                        {selectedItem.archived_at 
                          ? new Date(selectedItem.archived_at).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                          : '-'}
                      </p>
                    </div>
                  </div>
                  {selectedItem.archive_reason && (
                    <div className="mt-3">
                      <Label className="text-gray-500">Arşiv Nedeni</Label>
                      <p className="font-medium text-orange-800 bg-orange-100 p-2 rounded mt-1">{selectedItem.archive_reason}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Ekli Belgeler */}
              {selectedItem.documents && selectedItem.documents.length > 0 && (
                <div className="pt-4 border-t">
                  <Label className="text-gray-500 mb-2 block flex items-center gap-2">
                    <Paperclip className="h-4 w-4" />
                    Ekli Belgeler ({selectedItem.documents.length})
                  </Label>
                  <div className="space-y-2">
                    {selectedItem.documents.map((doc, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-blue-100 rounded-lg">
                            <File className="h-5 w-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-700">{doc.name}</p>
                            <p className="text-xs text-gray-400">{formatFileSize(doc.size)}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {doc.data && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  const newWindow = window.open();
                                  if (newWindow) {
                                    newWindow.document.write(`
                                      <html>
                                        <head><title>${doc.name}</title></head>
                                        <body style="margin:0;display:flex;justify-content:center;align-items:center;min-height:100vh;background:#f5f5f5;">
                                          ${doc.type?.startsWith('image/') 
                                            ? `<img src="${doc.data}" style="max-width:100%;max-height:100vh;" />`
                                            : doc.type === 'application/pdf'
                                              ? `<iframe src="${doc.data}" style="width:100%;height:100vh;border:none;"></iframe>`
                                              : `<div style="padding:20px;background:white;border-radius:8px;box-shadow:0 2px 10px rgba(0,0,0,0.1);"><p>Bu dosya türü önizlenemez.</p><p>Lütfen indirin.</p></div>`
                                          }
                                        </body>
                                      </html>
                                    `);
                                  }
                                }}
                                className="gap-1"
                                data-testid={`view-report-doc-${index}`}
                              >
                                <Eye className="h-4 w-4" />
                                Görüntüle
                              </Button>
                              <Button
                                variant="default"
                                size="sm"
                                onClick={() => {
                                  const link = document.createElement('a');
                                  link.href = doc.data;
                                  link.download = doc.name;
                                  document.body.appendChild(link);
                                  link.click();
                                  document.body.removeChild(link);
                                }}
                                className="gap-1 bg-blue-600 hover:bg-blue-700"
                                data-testid={`download-report-doc-${index}`}
                              >
                                <Download className="h-4 w-4" />
                                İndir
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {selectedItem && selectedType === 'request' && (
            <div className="space-y-4">
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h3 className="font-bold text-lg mb-2">{selectedItem.title}</h3>
                {selectedItem.description && (
                  <p className="text-gray-600">{selectedItem.description}</p>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">İsteyen</Label>
                  <p className="font-medium">{selectedItem.requester?.name || selectedItem.requester_name || '-'}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Hedef</Label>
                  <p className="font-medium">{selectedItem.target_user?.name || selectedItem.target_user_name || '-'}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Oluşturulma Tarihi</Label>
                  <p className="font-medium">
                    {selectedItem.created_at ? (
                      <>{new Date(selectedItem.created_at).toLocaleDateString('tr-TR')} - {new Date(selectedItem.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</>
                    ) : '-'}
                  </p>
                </div>
                <div>
                  <Label className="text-gray-500">Durum</Label>
                  <div className="mt-1">{getStatusBadge(selectedItem.status)}</div>
                </div>
                {selectedItem.due_date && (
                  <div>
                    <Label className="text-gray-500">Son Tarih</Label>
                    <p className="font-medium">{new Date(selectedItem.due_date).toLocaleDateString('tr-TR')}</p>
                  </div>
                )}
              </div>

              {selectedItem.direction === 'received' && selectedItem.status === 'pending' && (
                <div className="flex gap-2 pt-4 border-t">
                  <Button 
                    onClick={() => handleUpdateReportRequest(selectedItem.id, 'accepted')}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    data-testid="accept-request-btn"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Kabul Et
                  </Button>
                  <Button 
                    onClick={() => handleUpdateReportRequest(selectedItem.id, 'rejected')}
                    variant="destructive"
                    className="flex-1"
                    data-testid="reject-request-btn"
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Reddet
                  </Button>
                </div>
              )}

              {/* Kabul edilen istekler için Cevapla butonu */}
              {selectedItem.direction === 'received' && selectedItem.status === 'accepted' && (
                <div className="flex gap-2 pt-4 border-t">
                  <Button 
                    onClick={() => handleOpenRespondModal(selectedItem)}
                    className="flex-1 bg-purple-600 hover:bg-purple-700"
                    data-testid="respond-request-btn"
                  >
                    <Reply className="h-4 w-4 mr-2" />
                    Cevapla
                  </Button>
                </div>
              )}

              {/* Cevaplandı durumundaki istekler için cevap bilgileri */}
              {selectedItem.status === 'completed' && (
                <div className="pt-4 border-t">
                  <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                    <div className="flex items-center gap-2 mb-3">
                      <Reply className="h-5 w-5 text-green-600" />
                      <span className="font-semibold text-green-700 text-lg">Cevap Bilgileri</span>
                    </div>
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-gray-500">Cevaplayan:</span>{' '}
                          <span className="font-medium">{selectedItem.responded_by_name || '-'}</span>
                        </div>
                        {selectedItem.responded_at && (
                          <div>
                            <span className="text-gray-500">Cevap Tarihi:</span>{' '}
                            <span className="font-medium">
                              {new Date(selectedItem.responded_at).toLocaleDateString('tr-TR')} - {new Date(selectedItem.responded_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                        )}
                      </div>
                      {selectedItem.response_title && (
                        <div className="bg-white p-3 rounded border">
                          <span className="text-gray-500 text-sm block mb-1">Cevap Başlığı:</span>
                          <span className="font-semibold">{selectedItem.response_title}</span>
                        </div>
                      )}
                      {selectedItem.response_content && (
                        <div className="bg-white p-3 rounded border">
                          <span className="text-gray-500 text-sm block mb-1">Cevap İçeriği:</span>
                          <p className="whitespace-pre-wrap">{selectedItem.response_content}</p>
                        </div>
                      )}
                      
                      {/* Cevap Belgeleri */}
                      {selectedItem.response_documents && selectedItem.response_documents.length > 0 && (
                        <div className="bg-white p-3 rounded border">
                          <div className="flex items-center gap-2 mb-2">
                            <Paperclip className="h-4 w-4 text-blue-500" />
                            <span className="text-gray-500 text-sm">Cevap Belgeleri ({selectedItem.response_documents.length})</span>
                          </div>
                          <div className="space-y-2">
                            {selectedItem.response_documents.map((doc, index) => (
                              <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded border">
                                <div className="flex items-center gap-2">
                                  <File className="h-4 w-4 text-blue-500" />
                                  <span className="text-sm truncate max-w-[150px]">{doc.name}</span>
                                  {doc.size && <span className="text-xs text-gray-400">({formatFileSize(doc.size)})</span>}
                                </div>
                                <div className="flex gap-1">
                                  {doc.type?.startsWith('image/') && doc.data && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => window.open(doc.data, '_blank')}
                                      className="gap-1"
                                    >
                                      <Eye className="h-3 w-3" />
                                      Önizle
                                    </Button>
                                  )}
                                  {doc.data && (
                                    <Button
                                      size="sm"
                                      onClick={() => {
                                        const link = document.createElement('a');
                                        link.href = doc.data;
                                        link.download = doc.name;
                                        document.body.appendChild(link);
                                        link.click();
                                        document.body.removeChild(link);
                                      }}
                                      className="gap-1 bg-blue-600 hover:bg-blue-700"
                                    >
                                      <Download className="h-3 w-3" />
                                      İndir
                                    </Button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Cevap Verme Modalı */}
      <Dialog open={respondModalOpen} onOpenChange={setRespondModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Reply className="h-5 w-5 text-purple-600" />
              Rapor İsteğine Cevap Ver
            </DialogTitle>
          </DialogHeader>
          
          {selectedItem && (
            <div className="space-y-4">
              {/* İstek Bilgileri */}
              <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                <p className="text-sm text-purple-600 font-medium mb-1">İstek Bilgileri</p>
                <p className="font-semibold">{selectedItem.title}</p>
                {selectedItem.description && (
                  <p className="text-sm text-gray-600 mt-1">{selectedItem.description}</p>
                )}
                <p className="text-xs text-gray-500 mt-2">
                  İsteyen: {selectedItem.requester_name || selectedItem.requester?.name} • {selectedItem.created_at && new Date(selectedItem.created_at).toLocaleDateString('tr-TR')}
                </p>
              </div>

              {/* Cevap Formu */}
              <div>
                <Label htmlFor="response-title">Rapor Başlığı *</Label>
                <Input 
                  id="response-title"
                  value={responseForm.title}
                  onChange={(e) => setResponseForm({ ...responseForm, title: e.target.value })}
                  placeholder="Rapor başlığını girin"
                  data-testid="response-title-input"
                />
              </div>
              
              <div>
                <Label htmlFor="response-content">Rapor İçeriği</Label>
                <Textarea 
                  id="response-content"
                  value={responseForm.content}
                  onChange={(e) => setResponseForm({ ...responseForm, content: e.target.value })}
                  placeholder="Rapor içeriği"
                  rows={5}
                  data-testid="response-content-input"
                />
              </div>

              {/* Belge Yükleme */}
              <div>
                <Label className="flex items-center gap-2">
                  <Paperclip className="h-4 w-4 text-blue-500" />
                  Belge Ekle
                </Label>
                <div className="mt-2">
                  <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50 border-gray-300">
                    <div className="flex flex-col items-center justify-center pt-2 pb-2">
                      <Upload className="w-6 h-6 mb-1 text-gray-400" />
                      <p className="text-sm text-gray-500">Belge yüklemek için tıklayın</p>
                      <p className="text-xs text-gray-400">PDF, JPG, PNG, DOC, XLS</p>
                    </div>
                    <input
                      type="file"
                      className="hidden"
                      multiple
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                      onChange={handleResponseDocUpload}
                      data-testid="response-file-input"
                    />
                  </label>
                </div>
                {responseForm.documents.length > 0 && (
                  <div className="mt-2 space-y-2">
                    {responseForm.documents.map((doc, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-center gap-2">
                          <File className="h-4 w-4 text-blue-500" />
                          <span className="text-sm truncate max-w-[200px]">{doc.name}</span>
                          <span className="text-xs text-gray-400">({formatFileSize(doc.size)})</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeResponseDocument(index)}
                          className="text-red-500 hover:text-red-700 p-1"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setRespondModalOpen(false)}>
              İptal
            </Button>
            <Button 
              onClick={handleSubmitResponse}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="submit-response-btn"
            >
              <Send className="h-4 w-4 mr-2" />
              Cevabı Gönder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Silme Onay Modalı */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              {deleteType === 'report' ? 'Raporları Sil' : 'Rapor İsteklerini Sil'}
            </DialogTitle>
            <DialogDescription>
              {deleteType === 'report' 
                ? `${selectedReports.length} adet raporu silmek istediğinizden emin misiniz?`
                : `${selectedRequests.length} adet rapor isteğini silmek istediğinizden emin misiniz?`
              } Bu işlem geri alınamaz.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowDeleteModal(false)} disabled={deleting}>
              İptal
            </Button>
            <Button variant="destructive" onClick={handleDeleteSelected} disabled={deleting}>
              {deleting ? 'Siliniyor...' : `Evet, Sil`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Taşerona Taşı Modal */}
      <Dialog open={moveModalOpen} onOpenChange={setMoveModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MoveRight className="h-5 w-5 text-blue-600" />
              Raporu Taşerona Taşı
            </DialogTitle>
            <DialogDescription>
              Bu raporu hangi taşerona taşımak istiyorsunuz?
            </DialogDescription>
          </DialogHeader>
          
          {selectedReportToMove && (
            <div className="space-y-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">Taşınacak Rapor:</p>
                <p className="font-medium">{selectedReportToMove.title}</p>
              </div>
              
              <div className="space-y-2">
                <Label>Taşeron Seçin</Label>
                <Select value={selectedSubcontractorId} onValueChange={setSelectedSubcontractorId}>
                  <SelectTrigger data-testid="subcontractor-select">
                    <SelectValue placeholder="Taşeron seçin..." />
                  </SelectTrigger>
                  <SelectContent>
                    {subcontractors.length === 0 ? (
                      <SelectItem value="none" disabled>Taşeron bulunamadı</SelectItem>
                    ) : (
                      subcontractors.map(sub => (
                        <SelectItem key={sub.id} value={sub.id}>
                          {sub.company_name || sub.name}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setMoveModalOpen(false)}>
              İptal
            </Button>
            <Button 
              onClick={handleMoveToSubcontractor}
              disabled={!selectedSubcontractorId}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="confirm-move-btn"
            >
              <MoveRight className="h-4 w-4 mr-2" />
              Taşı
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Arşiv Açıklaması Modal */}
      <Dialog open={archiveReasonModalOpen} onOpenChange={setArchiveReasonModalOpen}>
        <DialogContent className="max-w-md" data-testid="archive-reason-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <Archive className="h-5 w-5" />
              Raporu Arşive Gönder
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-600">
              Bu raporu arşive göndermek için bir açıklama yazmanız gerekmektedir.
            </p>
            <div className="space-y-2">
              <Label htmlFor="archive-reason" className="text-sm font-medium">
                Arşiv Açıklaması <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="archive-reason"
                placeholder="Arşivleme nedenini yazın..."
                value={archiveReason}
                onChange={(e) => setArchiveReason(e.target.value)}
                className="min-h-[100px]"
                data-testid="archive-reason-input"
              />
              <p className="text-xs text-gray-500">
                Bu açıklama arşivlenen raporun detaylarında görünecektir.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setArchiveReasonModalOpen(false);
                setArchiveReason('');
                setArchiveTargetId(null);
              }}
              disabled={archivingItem}
            >
              İptal
            </Button>
            <Button 
              onClick={handleConfirmArchiveReport}
              className="bg-orange-600 hover:bg-orange-700"
              disabled={archivingItem || !archiveReason.trim()}
              data-testid="confirm-archive-btn"
            >
              {archivingItem ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Archive className="h-4 w-4 mr-2" />
              )}
              Arşive Gönder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BranchReports;
